import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Grid,
  Tabs,
  Tab,
  Chip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, RestoreFromTrash as RestoreIcon, FilterListOff as FilterListOffIcon } from '@mui/icons-material';
import { Atelier, AtelierCreate, AtelierUpdate } from '../../../types/atelier';
import { ateliersAPI } from '../../../services/api';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import AlertDialog from '../../../components/AlertDialog';
import useAlert from '../../../hooks/useAlert';
import PermissionButton from '../../../components/PermissionButton';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';
import { extractErrorMessage } from '../../../utils/errorHandler';

const CONFIG_COLOR = '#1976D2';

const ATELIER_FILTER_SCHEMA = {
  code:        { type: 'text', param: 'code' },
  name:        { type: 'text', param: 'name' },
  description: { type: 'text', param: 'description' },
  is_active:   { type: 'select', param: 'is_active' },
} as const;

const ATELIER_STATUT_OPTIONS = [
  { value: 'true', label: 'Actif' },
  { value: 'false', label: 'Inactif' },
];

const AtelierManagement: React.FC = () => {
  const [ateliers, setAteliers] = useState<Atelier[]>([]);
  const [deletedAteliers, setDeletedAteliers] = useState<Atelier[]>([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingAtelier, setEditingAtelier] = useState<Atelier | null>(null);
  const [currentAtelier, setCurrentAtelier] = useState<Partial<Atelier>>({
    code: '',
    name: '',
    description: '',
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { filters, update, reset, hasActive } = useColumnFilters(ATELIER_FILTER_SCHEMA);

  const filteredAteliers = useMemo(
    () =>
      ateliers.filter((a) => {
        if (filters.code && !a.code.toLowerCase().includes(filters.code.toLowerCase())) return false;
        if (filters.name && !a.name.toLowerCase().includes(filters.name.toLowerCase())) return false;
        if (filters.description && !(a.description || '').toLowerCase().includes(filters.description.toLowerCase())) return false;
        if (filters.is_active !== '' && a.is_active !== (filters.is_active === 'true')) return false;
        return true;
      }),
    [ateliers, filters]
  );

  useEffect(() => {
    fetchAteliers();
  }, []);

  const fetchAteliers = async () => {
    try {
      setLoading(true);
      const data = await ateliersAPI.getAll();
      setAteliers(data.items);
    } catch (error) {
      showError('Erreur lors du chargement des ateliers');
      console.error('Error fetching ateliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedAteliers = async () => {
    try {
      setLoading(true);
      const data = await ateliersAPI.getDeleted();
      setDeletedAteliers(data.items);
    } catch (error) {
      showError('Erreur lors du chargement des ateliers supprimés');
      console.error('Error fetching deleted ateliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (atelier: Atelier) => {
    try {
      setLoading(true);
      await ateliersAPI.restore(atelier.id);
      showSuccess('Atelier restauré avec succès');
      fetchDeletedAteliers();
      fetchAteliers();
    } catch (error) {
      showError('Erreur lors de la restauration de l\'atelier');
      console.error('Error restoring atelier:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 1) {
      fetchDeletedAteliers();
    }
  };

  const handleOpen = async (atelier?: Atelier) => {
    if (atelier) {
      setEditingAtelier(atelier);
      setCurrentAtelier({
        code: atelier.code,
        name: atelier.name,
        description: atelier.description || '',
        is_active: atelier.is_active,
      });
      setEditMode(true);
    } else {
      setEditingAtelier(null);
      try {
        const nextCodeData = await ateliersAPI.getNextCode();
        setCurrentAtelier({
          code: nextCodeData.code,
          name: '',
          description: '',
          is_active: true,
        });
      } catch (error) {
        setCurrentAtelier({
          code: '',
          name: '',
          description: '',
          is_active: true,
        });
      }
      setEditMode(false);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingAtelier(null);
    setCurrentAtelier({
      code: '',
      name: '',
      description: '',
      is_active: true,
    });
    setEditMode(false);
  };

  const handleSave = async () => {
    if (!currentAtelier.code?.trim() || !currentAtelier.name?.trim()) {
      showError('Le code et le nom sont obligatoires');
      return;
    }

    try {
      setLoading(true);
      if (editMode && editingAtelier) {
        const updateData: AtelierUpdate = {
          code: currentAtelier.code,
          name: currentAtelier.name,
          description: currentAtelier.description || undefined,
          is_active: currentAtelier.is_active,
        };
        await ateliersAPI.update(editingAtelier.id, updateData);
        showSuccess('Atelier modifié avec succès');
      } else {
        const createData: AtelierCreate = {
          code: currentAtelier.code!,
          name: currentAtelier.name!,
          description: currentAtelier.description || undefined,
          is_active: currentAtelier.is_active,
        };
        await ateliersAPI.create(createData);
        showSuccess('Atelier créé avec succès');
      }
      await fetchAteliers();
      handleClose();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (atelier: Atelier) => {
    showConfirm(
      `Êtes-vous sûr de vouloir supprimer l'atelier "${atelier.name}" ?`,
      async () => {
        try {
          setLoading(true);
          await ateliersAPI.delete(atelier.id);
          showSuccess('Atelier supprimé avec succès');
          await fetchAteliers();
        } catch (error) {
          showError('Erreur lors de la suppression de l\'atelier');
          console.error('Error deleting atelier:', error);
        } finally {
          setLoading(false);
        }
      }
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Gestion des Ateliers</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {tabValue === 0 && hasActive() && (
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={reset}
              sx={{ color: 'text.secondary' }}
            >
              Effacer les filtres
            </Button>
          )}
          {tabValue === 0 && (
            <PermissionButton
              moduleName="Config"
              operation="add"
              miniModuleName="Ateliers"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpen()}
            >
              Nouvel Atelier
            </PermissionButton>
          )}
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Ateliers Actifs" />
          <Tab label="Corbeille" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Box sx={getModuleTableTheme('configuration')}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Code
                      <ColumnFilter type="text" value={filters.code} onChange={(v) => update('code', v)} label="code" color={CONFIG_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Nom
                      <ColumnFilter type="text" value={filters.name} onChange={(v) => update('name', v)} label="nom" color={CONFIG_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Description
                      <ColumnFilter type="text" value={filters.description} onChange={(v) => update('description', v)} label="description" color={CONFIG_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Statut
                      <ColumnFilter type="select" value={filters.is_active} onChange={(v) => update('is_active', v)} options={ATELIER_STATUT_OPTIONS} label="statut" color={CONFIG_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAteliers.map((atelier) => (
                  <TableRow key={atelier.id}>
                    <TableCell>{atelier.code}</TableCell>
                    <TableCell>{atelier.name}</TableCell>
                    <TableCell>{atelier.description || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={atelier.is_active ? 'Actif' : 'Inactif'}
                        color={atelier.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <PermissionButton
                        moduleName="Config"
                        operation="edit"
                        miniModuleName="Ateliers"
                        component="IconButton"
                        onClick={() => handleOpen(atelier)}
                        color="primary"
                      >
                        <EditIcon />
                      </PermissionButton>
                      <PermissionButton
                        moduleName="Config"
                        operation="delete"
                        miniModuleName="Ateliers"
                        component="IconButton"
                        onClick={() => handleDelete(atelier)}
                        color="error"
                      >
                        <DeleteIcon />
                      </PermissionButton>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredAteliers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {hasActive() ? 'Aucun atelier ne correspond aux filtres' : 'Aucun atelier trouvé'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tabValue === 1 && (
        <Box sx={getModuleTableTheme('configuration')}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Nom</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Date de Suppression</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deletedAteliers.map((atelier) => (
                  <TableRow key={atelier.id}>
                    <TableCell>{atelier.code}</TableCell>
                    <TableCell>{atelier.name}</TableCell>
                    <TableCell>{atelier.description || '-'}</TableCell>
                    <TableCell>
                      {atelier.deleted_at ? new Date(atelier.deleted_at).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => {
                          showConfirm(
                            `Êtes-vous sûr de vouloir restaurer l'atelier "${atelier.name}" ?`,
                            () => handleRestore(atelier),
                            {
                              title: 'Confirmer la restauration',
                              confirmText: 'Restaurer',
                              cancelText: 'Annuler',
                            }
                          );
                        }}
                        color="primary"
                        title="Restaurer"
                      >
                        <RestoreIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {deletedAteliers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Aucun atelier supprimé
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Modifier l\'Atelier' : 'Nouvel Atelier'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                variant="filled"
                fullWidth
                label="Code"
                value={currentAtelier.code || ''}
                onChange={(e) => setCurrentAtelier({ ...currentAtelier, code: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                variant="filled"
                fullWidth
                label="Nom"
                value={currentAtelier.name || ''}
                onChange={(e) => setCurrentAtelier({ ...currentAtelier, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="filled"
                fullWidth
                label="Description"
                value={currentAtelier.description || ''}
                onChange={(e) => setCurrentAtelier({ ...currentAtelier, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentAtelier.is_active ?? true}
                    onChange={(e) => setCurrentAtelier({ ...currentAtelier, is_active: e.target.checked })}
                  />
                }
                label="Actif"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {editMode ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default AtelierManagement;
